import { useState, useEffect } from 'react'
import { ListChecks, FlaskConical, Check, Thermometer, Clock } from 'lucide-react'
import { useLab } from '../../../context/LabContext'
import { Beaker, HotPlate, Timer } from '../../lab'
import InfoBox from '../InfoBox'

const steps = [
  { id: 1, title: 'Measure Cola', short: 'Click graduated cylinder to measure ~100 mL' },
  { id: 2, title: 'Transfer to Beaker', short: 'Click beaker and cover with watch glass' },
  { id: 3, title: 'Begin Boiling', short: 'Click hot plate to boil for 10 minutes' },
  { id: 4, title: 'Monitor Decarbonation', short: 'Watch for bubbling to stop (10 min)' },
  { id: 5, title: 'Cool Sample', short: 'Click to cool to room temperature' },
  { id: 6, title: 'Prepare Cola for Analysis', short: 'Add 0.5 mL conc. H₂SO₄ and dilute 50-fold' }
]

// Cola color: dark brown
const COLA_COLOR = '#3D2314'

const Part1SamplePrep = () => {
  const { state, updateExp3Part1, completeStep, update } = useLab()
  const { exp3Part1, currentStep, labMode, completedSteps } = state

  const [showInfo, setShowInfo] = useState<number | null>(null)
  const [transferring, setTransferring] = useState(false)
  const [timerRunning, setTimerRunning] = useState(false)
  const [timerValue, setTimerValue] = useState(0)
  const [bubbles, setBubbles] = useState<Array<{ id: number; x: number; delay: number }>>([])

  // Fix: Reset currentStep to first incomplete step when this part mounts
  useEffect(() => {
    if (labMode !== 'guided') return
    const firstIncomplete = steps.find(s => !completedSteps.includes(`samplePrep-${s.id}`))
    const targetStep = firstIncomplete ? firstIncomplete.id : steps[steps.length - 1].id
    if (currentStep !== targetStep) {
      update({ currentStep: targetStep })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = (s: number) => labMode === 'free' || currentStep === s
  const isDone = (s: number) => completedSteps.includes(`samplePrep-${s}`)

  // Generate bubbles for decarbonation animation
  useEffect(() => {
    if (exp3Part1.heatingProgress > 0 && exp3Part1.heatingProgress < 100 && !exp3Part1.decarbonated) {
      const interval = setInterval(() => {
        setBubbles(prev => {
          const newBubbles = [...prev]
          // Add new bubble
          if (Math.random() > 0.3) {
            newBubbles.push({
              id: Date.now(),
              x: 30 + Math.random() * 40,
              delay: Math.random() * 0.5
            })
          }
          // Remove old bubbles
          return newBubbles.slice(-15)
        })
      }, 200)
      return () => clearInterval(interval)
    } else {
      setBubbles([])
    }
  }, [exp3Part1.heatingProgress, exp3Part1.decarbonated])

  // Timer and heating effect
  useEffect(() => {
    if (!timerRunning || exp3Part1.decarbonated) return
    const t = setInterval(() => {
      setTimerValue(v => {
        const newVal = v + 1
        // Progress heating (simulate 10-15 minutes in ~60 seconds for demo)
        const progress = Math.min((newVal / 60) * 100, 100)
        const temp = 25 + (progress / 100) * 75 // Goes up to 100C

        updateExp3Part1({
          heatingProgress: progress,
          temperature: Math.round(temp)
        })

        if (progress >= 100) {
          updateExp3Part1({ decarbonated: true })
          setTimerRunning(false)
          completeStep('samplePrep-4')
        }

        return newVal
      })
    }, 50) // Fast for demo
    return () => clearInterval(t)
  }, [timerRunning, exp3Part1.decarbonated, updateExp3Part1, completeStep])

  // Handlers
  const handleMeasureCola = () => {
    if (!isActive(1)) return
    updateExp3Part1({ colaVolume: 100 })
    completeStep('samplePrep-1')
  }

  const handleTransfer = () => {
    if (!isActive(2) || exp3Part1.colaVolume === 0) return
    setTransferring(true)
    setTimeout(() => {
      setTransferring(false)
      completeStep('samplePrep-2')
    }, 1000)
  }

  const handleStartHeating = () => {
    if (!isActive(3) || !isDone(2)) return
    setTimerRunning(true)
    completeStep('samplePrep-3')
  }

  const handleCool = () => {
    if (!isActive(5) || !exp3Part1.decarbonated) return
    updateExp3Part1({ temperature: 25, cooled: true })
    completeStep('samplePrep-5')
  }

  const handleDilute = () => {
    if (!isActive(6) || !exp3Part1.cooled) return
    updateExp3Part1({ diluted: true })
    completeStep('samplePrep-6')
  }

  const completedCount = completedSteps.filter((s: string) => s.startsWith('samplePrep')).length

  return (
    <div className="part1-sample-prep">
      {/* Steps Panel */}
      <div className="steps-panel">
        <div className="steps-header">
          <ListChecks size={16} />
          <h3>Part I: Sample Preparation</h3>
        </div>
        <div className="steps-list">
          {steps.map(s => (
            <div
              key={s.id}
              className={`step-item ${isActive(s.id) && !isDone(s.id) ? 'active' : ''}`}
            >
              <div className={`step-number ${isDone(s.id) ? 'done' : isActive(s.id) ? 'current' : ''}`}>
                {isDone(s.id) ? <Check size={12} /> : s.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className={`step-title ${isActive(s.id) ? 'highlight' : ''}`}>
                    {s.title}
                  </span>
                  <button
                    className="step-info-btn"
                    onClick={(e) => { e.stopPropagation(); setShowInfo(s.id) }}
                  >
                    ?
                  </button>
                </div>
                {isActive(s.id) && !isDone(s.id) && (
                  <span className="step-hint">{s.short}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lab Bench */}
      <div className="lab-bench">
        <div className="bench-header">
          <div className="bench-title">
            <FlaskConical size={16} />
            <h3>Lab Bench</h3>
          </div>
          <span className="bench-progress">{completedCount}/6 Complete</span>
        </div>

        <div className="bench-area">
          {/* Heating progress bar */}
          {exp3Part1.heatingProgress > 0 && !exp3Part1.decarbonated && (
            <div className="heating-progress">
              <div
                className="heating-bar"
                style={{ width: `${exp3Part1.heatingProgress}%` }}
              />
              <span>Decarbonating: {Math.round(exp3Part1.heatingProgress)}%</span>
            </div>
          )}

          {/* Temperature display */}
          {timerRunning && (
            <div className="temp-display">
              <Thermometer size={16} />
              <span>{exp3Part1.temperature}°C</span>
            </div>
          )}

          {/* Equipment Layout */}
          <div className="equipment-grid">
            {/* Graduated Cylinder (for measuring cola) */}
            <div
              className={`graduated-cylinder-container ${isActive(1) && !isDone(1) ? 'clickable' : ''}`}
              onClick={handleMeasureCola}
              style={{
                cursor: isActive(1) && !isDone(1) ? 'pointer' : 'default',
                opacity: isDone(1) ? 0.5 : 1
              }}
            >
              <svg
                viewBox="0 0 60 140"
                width={60}
                height={140}
                className={isActive(1) && !isDone(1) ? 'equipment-active' : ''}
              >
                {/* Graduated cylinder body */}
                <rect x="15" y="10" width="30" height="110" fill="rgba(200,220,240,0.3)" stroke="#7A9BB8" strokeWidth="2" rx="2" />
                {/* Cola fill */}
                {exp3Part1.colaVolume > 0 && (
                  <rect x="17" y="45" width="26" height="72" fill={COLA_COLOR} rx="1" />
                )}
                {/* Graduation marks */}
                {[20, 40, 60, 80, 100].map(mark => {
                  const y = 117 - (mark / 100) * 75
                  return (
                    <g key={mark}>
                      <line x1="40" y1={y} x2="45" y2={y} stroke="#5A7A95" strokeWidth="1" />
                      <text x="47" y={y + 3} fontSize="7" fill="#5A7A95">{mark}</text>
                    </g>
                  )
                })}
                {/* Label */}
                <text x="30" y="135" textAnchor="middle" fontSize="9" fill="var(--text-primary)">~100 mL</text>
              </svg>
              <span className="equipment-label">Graduated Cylinder</span>
            </div>

            {/* Erlenmeyer Flask on Hot Plate */}
            <div className="flask-hotplate-container">
              <div
                className={`erlenmeyer-flask ${isActive(2) && !isDone(2) ? 'clickable' : ''}`}
                onClick={isActive(2) && !isDone(2) ? handleTransfer : undefined}
                style={{
                  cursor: isActive(2) && !isDone(2) ? 'pointer' : 'default'
                }}
              >
                <svg
                  viewBox="0 0 100 120"
                  width={100}
                  height={120}
                  style={{ position: 'relative', zIndex: 2 }}
                  className={isActive(2) && !isDone(2) ? 'equipment-active' : ''}
                >
                  {/* Flask body - Erlenmeyer shape */}
                  <path
                    d="M35 20 L35 45 L15 100 Q15 110 50 110 Q85 110 85 100 L65 45 L65 20 Z"
                    fill="rgba(200,220,240,0.3)"
                    stroke="#7A9BB8"
                    strokeWidth="2"
                  />
                  {/* Neck */}
                  <rect x="35" y="5" width="30" height="18" fill="rgba(200,220,240,0.3)" stroke="#7A9BB8" strokeWidth="2" />
                  {/* Rim */}
                  <ellipse cx="50" cy="5" rx="17" ry="3" fill="#8AAFC8" stroke="#6A8AA5" strokeWidth="1" />

                  {/* Cola in flask (after transfer) */}
                  {isDone(2) && (
                    <>
                      <path
                        d="M20 95 L38 55 L62 55 L80 95 Q80 105 50 105 Q20 105 20 95 Z"
                        fill={COLA_COLOR}
                        opacity={exp3Part1.diluted ? 0.5 : 1}
                      />
                      {/* Bubbles during heating */}
                      {bubbles.map(bubble => (
                        <circle
                          key={bubble.id}
                          cx={bubble.x}
                          cy={90}
                          r={2 + Math.random() * 2}
                          fill="rgba(255,255,255,0.6)"
                          style={{
                            animation: `bubble-rise 1s ease-out ${bubble.delay}s forwards`
                          }}
                        />
                      ))}
                    </>
                  )}

                  {/* Glass highlight */}
                  <path
                    d="M22 85 L36 50"
                    stroke="rgba(255,255,255,0.4)"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>

              <HotPlate
                temperature={exp3Part1.temperature}
                isOn={timerRunning || (exp3Part1.heatingProgress > 0 && !exp3Part1.cooled)}
                setting={timerRunning ? 'high' : 'low'}
                hasVessel={isDone(2)}
                onClick={isActive(3) && isDone(2) && !isDone(3) ? handleStartHeating : undefined}
                disabled={!isActive(3) || isDone(3)}
              />
            </div>

            {/* Timer */}
            <div className="timer-container">
              <Timer
                time={timerValue}
                running={timerRunning}
                onClick={() => {}}
              />
              <span className="equipment-label">Timer</span>
            </div>

            {/* Cool button (after decarbonation) */}
            {exp3Part1.decarbonated && !exp3Part1.cooled && (
              <button
                className="action-button cool-button"
                onClick={handleCool}
              >
                <Thermometer size={18} />
                Cool to Room Temp
              </button>
            )}

            {/* Dilute button (after cooling) */}
            {exp3Part1.cooled && !exp3Part1.diluted && (
              <button
                className="action-button dilute-button"
                onClick={handleDilute}
              >
                <FlaskConical size={18} />
                Add H₂SO₄ & Dilute 50-fold
              </button>
            )}

            {/* Completion message */}
            {exp3Part1.diluted && (
              <div className="completion-message">
                <Check size={24} />
                <span>Sample Preparation Complete!</span>
                <p>Proceed to Part II: Standard Preparation</p>
              </div>
            )}
          </div>

          {/* Bench surface */}
          <div className="bench-surface" />
        </div>
      </div>

      {/* Info Box */}
      {showInfo && (
        <InfoBox
          step={showInfo}
          experimentId="exp3"
          stepInfo={getInfoContent(showInfo)}
          onClose={() => setShowInfo(null)}
        />
      )}

      <style>{`
        .part1-sample-prep {
          display: flex;
          gap: 16px;
          flex: 1;
          width: 100%;
          min-height: 0;
          padding: 16px;
        }

        .steps-panel {
          width: 240px;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .steps-header {
          background: linear-gradient(135deg, var(--primary), var(--primary-dark));
          padding: 14px 16px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: white;
        }

        .steps-header h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .steps-list {
          flex: 1;
          overflow-y: auto;
          padding: 12px;
        }

        .step-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 8px;
          margin-bottom: 4px;
          transition: background 0.15s;
        }

        .step-item.active {
          background: rgba(255, 90, 54, 0.15);
        }

        .step-number {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
          flex-shrink: 0;
          background: var(--border);
          color: var(--text-muted);
        }

        .step-number.done {
          background: var(--secondary);
          color: white;
        }

        .step-number.current {
          background: var(--primary);
          color: white;
        }

        .step-content {
          flex: 1;
          min-width: 0;
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-title {
          font-size: 12px;
          font-weight: 600;
        }

        .step-title.highlight {
          color: var(--primary-light);
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
          border: none;
          cursor: pointer;
        }

        .step-info-btn:hover {
          transform: scale(1.1);
        }

        .step-hint {
          font-size: 11px;
          color: var(--text-secondary);
          margin-top: 4px;
          display: block;
        }

        .lab-bench {
          flex: 1;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: auto;
        }

        .bench-header {
          background: linear-gradient(135deg, var(--secondary), var(--secondary-dark));
          padding: 12px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: white;
        }

        .bench-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .bench-title h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .bench-progress {
          font-size: 12px;
          opacity: 0.9;
        }

        .bench-area {
          flex: 1;
          background: linear-gradient(180deg, var(--bg-elevated) 0%, var(--bg-surface) 100%);
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .heating-progress {
          position: absolute;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          width: 200px;
          height: 24px;
          background: var(--border);
          border-radius: 12px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .heating-bar {
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          background: linear-gradient(90deg, #FF8C00, #FF4500);
          transition: width 0.3s;
        }

        .heating-progress span {
          position: relative;
          font-size: 12px;
          font-weight: 600;
          color: white;
          text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        }

        .temp-display {
          position: absolute;
          top: 55px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 69, 0, 0.2);
          padding: 8px 16px;
          border-radius: 8px;
          color: #FF4500;
          font-weight: 600;
        }

        .equipment-grid {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-end;
          justify-content: center;
          gap: 40px;
          margin-bottom: 60px;
        }

        .graduated-cylinder-container,
        .timer-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .equipment-label {
          font-size: 11px;
          color: var(--text-secondary);
        }

        .flask-hotplate-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
        }

        .erlenmeyer-flask {
          position: relative;
          margin-bottom: -20px;
          z-index: 2;
        }

        .equipment-active {
          animation: glow 1.5s ease-in-out infinite;
        }

        .clickable {
          transition: transform 0.2s ease;
        }

        .clickable:hover {
          transform: scale(1.05);
        }

        @keyframes glow {
          0%, 100% { filter: drop-shadow(0 0 8px var(--primary)); }
          50% { filter: drop-shadow(0 0 15px var(--primary)); }
        }

        @keyframes bubble-rise {
          0% {
            transform: translateY(0);
            opacity: 0.8;
          }
          100% {
            transform: translateY(-40px);
            opacity: 0;
          }
        }

        .action-button {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 24px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          transition: all 0.2s;
        }

        .cool-button {
          background: #4FC3F7;
          color: white;
        }

        .cool-button:hover {
          background: #29B6F6;
          transform: translateY(-2px);
        }

        .dilute-button {
          background: var(--secondary);
          color: white;
        }

        .dilute-button:hover {
          background: var(--secondary-dark);
          transform: translateY(-2px);
        }

        .completion-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 24px;
          background: rgba(0, 168, 107, 0.15);
          border-radius: 12px;
          border: 2px solid var(--secondary);
        }

        .completion-message svg {
          color: var(--secondary);
        }

        .completion-message span {
          font-size: 16px;
          font-weight: 600;
          color: var(--secondary);
        }

        .completion-message p {
          font-size: 12px;
          color: var(--text-secondary);
          margin: 0;
        }

        .bench-surface {
          display: none;
        }
      `}</style>
    </div>
  )
}

// Info content for each step
function getInfoContent(step: number) {
  const content: Record<number, { title: string; content: string }> = {
    1: {
      title: 'Sample Preparation',
      content: 'About 100 mL of the cola is placed in a beaker covered by a watch glass (for the entire class). CO₂ must be absent from the sample prior to analysis because effervescence causes problems in pipetting and volume measurements.'
    },
    2: {
      title: 'Transfer to Beaker',
      content: 'Transfer the cola to a beaker and cover with a watch glass. The watch glass prevents loss of sample during boiling while allowing CO₂ to escape.'
    },
    3: {
      title: 'Why Decarbonate?',
      content: 'Boil the cola for 10 minutes in a beaker covered by a watch glass. Effervescence of CO₂ causes problems in pipetting and other volume measurements, and hence CO₂ must be absent from the sample prior to analysis. Observe: Bubbling will decrease as CO₂ is driven off.'
    },
    4: {
      title: 'Monitoring Decarbonation',
      content: 'Heat the cola for 10 minutes. You will see bubbles of CO₂ escaping. Continue heating until the bubbling stops completely, indicating full decarbonation.'
    },
    5: {
      title: 'Cooling the Sample',
      content: 'Allow the sample to cool to room temperature before the next step. Hot solutions can affect volume measurements and the chemical reaction.'
    },
    6: {
      title: 'Cola Sample Preparation',
      content: 'Just prior to the analysis, add 0.5 mL of concentrated sulfuric acid to the cola sample. Dilute the cooled decarbonated cola sample 50-fold with deionised water. Three separate samples will be prepared and measured, and the average phosphoric acid concentration reported (accounting for dilution).'
    }
  }
  return content[step]
}

export default Part1SamplePrep
